// §6.4 — the macOS keymap, the "macos" column of the table.
//
// **`Meta` is the Command key here and the Windows key on a PC** (§6.4), which is what makes this
// skin usable on either: the chords are written once, and the *physical* key they land on is the
// keyboard service's business. That is also why nothing in this file says "Cmd".
//
// Three of these are unlike any other skin's, and each is the platform's own answer:
//
//  * **Spaces move on `Ctrl+Arrow`**, not `Meta+Ctrl+Arrow` — this is the platform where switching
//    Space is a first-class gesture, so it gets the shorter chord.
//  * **Snapping takes `Ctrl+Meta+Arrow`** as a result, because `Ctrl+Arrow` is spoken for. It is
//    written below in canonical order — **Meta, Ctrl, Alt, Shift, key** (D-031) — which is the
//    same chord §6.4's table writes the other way round, and the same physical keys either way.
//  * **Mission Control is `Ctrl+ArrowUp`**, which is the real binding and collides with nothing
//    here precisely because snapping moved out of the way.
//
// `Meta+W` (close) and `Meta+Q` (which is not bound at all) are the chords a browser takes first;
// §6.4's honest-limitation toast covers the first, and the window's ✕ always works.
import type { Keymap } from '@hangar/shared';

export const macosKeymap: Keymap = {
  id: 'macos',
  bindings: [
    { id: 'launcher.open', chord: 'Meta+Space', reserved: true },
    { id: 'switcher.windows', chord: 'Meta+Tab', reserved: true },
    { id: 'switcher.overview', chord: 'Ctrl+ArrowUp', reserved: true },
    { id: 'window.snapLeft', chord: 'Meta+Ctrl+ArrowLeft', reserved: true },
    { id: 'window.snapRight', chord: 'Meta+Ctrl+ArrowRight', reserved: true },
    { id: 'window.snapUp', chord: 'Meta+Ctrl+ArrowUp', reserved: true },
    { id: 'window.snapDown', chord: 'Meta+Ctrl+ArrowDown', reserved: true },
    { id: 'desktop.previous', chord: 'Ctrl+ArrowLeft', reserved: true },
    { id: 'desktop.next', chord: 'Ctrl+ArrowRight', reserved: true },
    { id: 'clipboard.history', chord: 'Meta+Shift+V', reserved: true },
    { id: 'notifications.center', chord: 'Meta+Shift+N', reserved: true },
    { id: 'shell.immersive', chord: 'F11', reserved: true },
    { id: 'shell.lock', chord: 'Meta+Ctrl+Q', reserved: true },
    { id: 'app.explorer', chord: 'Meta+Alt+Space', reserved: true },
    { id: 'app.settings', chord: 'Meta+,', reserved: true },
    { id: 'window.close', chord: 'Meta+W', reserved: true },
    // §34.4 (D-505) — Quick Notes' capture card, `⌘⌥N`. Written in canonical order (D-031) like
    // every other chord here; `Meta` is Command on a Mac and the Windows key on a PC, which is what
    // lets this skin declare the platform's own gesture without naming the platform.
    { id: 'notes.capture', chord: 'Meta+Alt+N', reserved: true },
  ],
};
